import clsx from 'clsx';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, createSearchParams, useNavigate, useSearchParams } from 'react-router-dom';

import {
  HeroBanner,
  Icon,
  LeisurePurchaseSummary,
  Modal,
  Skeleton,
  Stepper,
  useCartContext,
  useLeisurePurchaseContext
} from 'components';
import { routes } from 'router';
import { appService } from 'services';
import { setLeisureSteps, setPurchaseModalOpen } from 'store';

import leisureBannerImage from 'assets/img/arrivaj/leisure-banner.jpeg';

const LeisurePurchasePage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const storedLeisureSteps = useSelector((state) => state.leisurePurchase.steps);
  const purchaseModalOpen = useSelector((state) => state.cart.purchaseModalOpen);
  const { cart, cartLoading } = useCartContext();
  const { currentStep, navigateToStep, prev } = useLeisurePurchaseContext();
  const orderId = searchParams.get('orderId');
  const confirmationStep = location.pathname.includes(routes.leisurePurchase.children.confirmation.path);

  const handleStepClick = (stepIndex) => {
    if (stepIndex < currentStep.order) {
      const previousStep = storedLeisureSteps.find((step) => step.order === stepIndex);
      // Update step in the store
      dispatch(setLeisureSteps({ stepToUpdate: currentStep, targetStep: previousStep }));

      // Navigate to previous step
      navigateToStep(previousStep.slug);
    }
  };

  const goToResultsPage = () => {
    purchaseModalOpen && dispatch(setPurchaseModalOpen(false));
    navigate({
      pathname: routes.results.path,
      search: createSearchParams({ type: 'leisures' }).toString()
    });
  };

  return (
    <>
      <HeroBanner
        title={t('cart:yourCart')}
        image={{ frontUrl: leisureBannerImage }}
        mobileImage={{ frontUrl: leisureBannerImage }}
      />

      <div className="main-container bg-white py-20">
        {cartLoading && (
          <div className="min-h-[50vh]">
            <Skeleton classNames="w-full h-16 !mb-16" />
            <div className="flex flex-col items-start justify-between gap-4 lg:flex-row">
              <Skeleton classNames="w-full h-72" />
              <Skeleton classNames="hidden lg:block lg:shrink-0 lg:w-96 lg:max-w-sm lg:h-96 lg:rounded-xl" />
              <Skeleton classNames="fixed bottom-0 bg-gray-200 left-0 w-full h-32 !mb-0 lg:hidden" />
            </div>
          </div>
        )}

        {/* EMPTY CART MESSAGE */}
        {!cartLoading && isEmpty(cart?.lines) && !orderId && (
          <div className="flex flex-col items-center gap-4">
            <img
              src={appService.loadBrandImage('emptyCart')}
              alt=""
              width="320"
              height="200"
              className="w-72 object-cover"
            />
            <p className="uppercase text-sm">{t('cart:emptyCart')}</p>
            <p>{t('cart:add')}</p>

            {!purchaseModalOpen && (
              <button type="button" className="btn w-fit mx-auto" onClick={goToResultsPage}>
                {t('cart:keepShopping')}
              </button>
            )}
          </div>
        )}

        {/* LEISURE PURCHASE TUNNEL */}
        {!cartLoading && (!isEmpty(cart?.lines) || !!orderId) && (
          <>
            {!orderId && !confirmationStep && (
              <>
                <button type="button" className="w-fit" onClick={prev}>
                  <Icon name="angle-left" classes="text-gray-500 mr-2" />
                  <span>{t('buttons.goBack')}</span>
                </button>
                <Stepper steps={storedLeisureSteps} onStepClick={handleStepClick} className="my-6 lg:my-12" />
              </>
            )}
            <div className="lg:flex lg:gap-x-8">
              <div className="lg:grow">
                {/* Step Component */}
                <Outlet />
              </div>

              {/* Leisure Summary */}
              <div className="lg:shrink-0 lg:w-96 lg:max-w-64 lg:flex lg:flex-col lg:gap-8 xl:max-w-sm">
                <LeisurePurchaseSummary
                  className={clsx(
                    (currentStep.slug === 'validation' || currentStep.slug === 'payment') && 'validation-step'
                  )}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {/* WARNING MESSAGE IN CASE OF EXPIRED CART */}
      <Modal
        isOpen={purchaseModalOpen}
        headerHidden
        body={
          <>
            <p className="my-4">{t('cart:expiredReset')}</p>
            <button type="button" className="btn w-fit mx-auto" onClick={goToResultsPage}>
              {t('cart:keepShopping')}
            </button>
          </>
        }
      />
    </>
  );
};

export default LeisurePurchasePage;
